// ============================================================================
// MODAL — AdminCourseDetailModal.jsx
// Full course detail view for admin role.
// Replaces the inline modalVisible block in adminCourseManagement.jsx.
//
// Props:
//   visible              {bool}
//   onClose              {fn}
//   course               {object}   — the selectedCourse object
//   tags                 {string[]} — resolved tag list
//   hasTextbook          {bool}
//   courseCircuits       {array}
//   editingWeight        {object|null}  — { type, id }
//   tempWeight           {string}
//   onTempWeightChange   {fn(value)}
//   onWeightEdit         {fn(type, id, currentWeight)}
//   onWeightSave         {fn()}
//   onWeightCancel       {fn()}
//   calculateTotalWeight {fn() => number}
//   onEditCourse         {fn(course)}
//   onTogglePublish      {fn(course)}
//   onDuplicateCourse    {fn(course)}
//   onDeleteCourse       {fn()}        — opens the confirm alert
//   onOpenListModal      {fn(type)}    — opens ContentListModal
//   onNavigateCircuits   {fn()}        — navigate to circuits page
// ============================================================================

import React from 'react';
import { CourseDomains } from '../../types/courseDomains';

import './AdminCourseDetailModal.css';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const roundPct = (w) => Math.round((w || 0) * 1000) / 10;

// ── Grading weight row ────────────────────────────────────────────────────────
const WeightRow = ({ typeKey, itemId, itemTitle, idLabel, weight, editingWeight, tempWeight, onTempWeightChange, onWeightEdit, onWeightSave, onWeightCancel }) => {
  const isEditing = editingWeight?.type === typeKey && editingWeight?.id === itemId;
  return (
    <div className="acd-weight-row">
      <div className="acd-weight-info">
        <span className="acd-weight-id">{idLabel}</span>
        <span className="acd-weight-title">{itemTitle}</span>
      </div>
      <div className="acd-weight-ctrl">
        {isEditing ? (
          <div className="acd-weight-edit">
            <input
              type="number" min="0" max="100" step="0.1" autoFocus
              className="acd-weight-input"
              value={tempWeight}
              onChange={e => onTempWeightChange(e.target.value)}
            />
            <span className="acd-weight-pct">%</span>
            <button className="acd-wbtn acd-wbtn--save"   onClick={onWeightSave}>✓</button>
            <button className="acd-wbtn acd-wbtn--cancel" onClick={onWeightCancel}>✕</button>
          </div>
        ) : (
          <div className="acd-weight-display">
            <span className="acd-weight-val">{roundPct(weight)}%</span>
            <button className="acd-wbtn acd-wbtn--edit" onClick={() => onWeightEdit(typeKey, itemId, weight)}>✏️</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Meta item ─────────────────────────────────────────────────────────────────
const MetaItem = ({ icon, label, value, isAvatar, avatarSrc, avatarAlt }) => (
  <div className="acd-meta-item">
    {isAvatar
      ? <img src={avatarSrc || '/images/default-avatar.png'} alt={avatarAlt} className="acd-meta-avatar" />
      : <span className="acd-meta-icon">{icon}</span>
    }
    <div className="acd-meta-content">
      <span className="acd-meta-label">{label}</span>
      <span className="acd-meta-value">{value}</span>
    </div>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────
const AdminCourseDetailModal = ({
  visible, onClose,
  course, tags, hasTextbook, courseCanvases,
  editingWeight, tempWeight, onTempWeightChange,
  onWeightEdit, onWeightSave, onWeightCancel, calculateTotalWeight,
  onEditCourse, onTogglePublish, onDuplicateCourse, onDeleteCourse,
  onOpenListModal, onNavigateCanvases 
}) => {
  const [collapsed, setCollapsed] = React.useState({});
  const toggleCollapse = (key) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  if (!visible || !course) return null;

  const totalWeight    = calculateTotalWeight?.() ?? 0;
  const weightIsValid  = totalWeight === 100;

  const weightRowProps = { editingWeight, tempWeight, onTempWeightChange, onWeightEdit, onWeightSave, onWeightCancel };

  return (
    <div className="acd-overlay" onClick={onClose}>
      <div className="acd-shell" onClick={e => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="acd-header">
          <div className="acd-header-icon">🏛️</div>
          <div className="acd-header-text">
            <div className="acd-header-title">{course.title}</div>
            <div className="acd-header-sub">{course.courseId} · Admin View</div>
          </div>
          <div className="acd-header-badges">
            <span className={`acd-status-badge ${course.isPublished ? 'acd-status-badge--published' : 'acd-status-badge--draft'}`}>
              {course.isPublished ? '🟢 Published' : '⚪ Draft'}
            </span>
          </div>
          <button className="acd-close" onClick={onClose}>✕</button>
        </div>

        {/* ── Body ── */}
        <div className="acd-body">

          {/* Course Actions */}
          <div className="acd-section">
            <div className="acd-section-title">Actions</div>
            <div className="acd-actions-row">
              <button className="acd-action-btn" onClick={() => onEditCourse?.(course)}>
                <span>✏️</span> Edit
              </button>
              <button className="acd-action-btn" onClick={() => onTogglePublish?.(course)}>
                <span>{course.isPublished ? '📥' : '📤'}</span>
                {course.isPublished ? 'Unpublish' : 'Publish'}
              </button>
              <button className="acd-action-btn" onClick={() => onDuplicateCourse?.(course)}>
                <span>📋</span> Duplicate
              </button>
              <button className="acd-action-btn acd-action-btn--danger" onClick={onDeleteCourse}>
                <span>🗑️</span> Delete
              </button>
            </div>
          </div>

          {/* View Content */}
          <div className="acd-section">
            <div className="acd-section-title">📋 View Content</div>
            <div className="acd-content-grid">
              {[
                { type: 'lessons',   icon: '📖', label: 'Lessons',   count: course.lessons?.length   || 0 },
                { type: 'labs',      icon: '🔬', label: 'Labs',      count: course.labs?.length      || 0 },
                { type: 'quizzes',   icon: '📋', label: 'Quizzes',   count: course.quizzes?.length   || 0 },
                { type: 'exams',     icon: '📄', label: 'Exams',     count: course.exams?.length     || 0 },
                { type: 'capstones', icon: '📘', label: 'Capstones', count: course.capstones?.length || 0 },
              ].map(({ type, icon, label, count }) => (
                <button key={type} className="acd-content-btn" 
                  onClick={() => onOpenListModal?.(type)}>
                  {icon} {label} <span className="acd-content-count">({count})</span>
                </button>
              ))}
        
              <button className="acd-content-btn" 
                  style={{backgroundColor: 'aqua', width: 'fit-content'}}
                  onClick={onNavigateCanvases}>
                  {CourseDomains[course.category]?.buttonTitle || '🔧 My Circuits'}
                  <span className="acd-content-count">({courseCanvases?.length || 0})</span>
              </button>
                              
            </div>
          </div>

          {/* Course Information */}
          <div className="acd-section">
            <div className="acd-section-title-row">
              <div className="acd-section-title">📖 Course Information</div>
              {hasTextbook && (
                <a
                  href={`/courses/${course.category}/index.html`}
                  target="_blank" rel="noopener noreferrer"
                  className="acd-textbook-link"
                >
                  <img src="/images/prof_ix.png" alt="Prof-iX" className="acd-textbook-img" />
                  📚 Course eBook
                </a>
              )}
            </div>

            <div className="acd-meta-grid">
              <MetaItem isAvatar avatarSrc={course.instructorImageUrl} avatarAlt={course.instructorName} label="Instructor" value={course.instructorName} />
              <MetaItem icon="📧" label="Instructor Email" value={course.instructorEmail} />
              <MetaItem icon="📊" label="Status" value={course.isPublished ? 'Published' : 'Unpublished'} />
              <MetaItem icon="💵" label="Price" value={`${course.currency || ''} ${course.price || 'N/A'}`} />
              <MetaItem icon="📈" label="Level" value={course.level} />
              {course.duration && <MetaItem icon="⏱️" label="Duration" value={course.duration} />}
              {course.credits  && <MetaItem icon="📘" label="Credits"  value={course.credits}  />}
              {course.passingGrade && <MetaItem icon="🎯" label="Passing Grade" value={`${course.passingGrade}%`} />}
              <MetaItem icon="📂" label="Category"          value={course.category} />
              <MetaItem icon="👥" label="Enrolled Students" value={course.enrolledStudents} />
              <MetaItem icon="🔄" label="Last Updated"      value={fmtDate(course.updatedAt)} />
            </div>

            {(course.shortDescription || course.longDescription) && (
              <div className="acd-description">
                {course.shortDescription && <h4 className="acd-short-desc">{course.shortDescription}</h4>}
                {course.longDescription  && <p  className="acd-long-desc">{course.longDescription}</p>}
              </div>
            )}
          </div>

          {/* Prerequisites */}
          <div className="acd-section">
            <div className="acd-section-title">📱 Prerequisites</div>
            <div className="acd-tags">
              {course.prerequisites?.length > 0
                ? course.prerequisites.map((pre, i) => <span key={i} className="acd-tag acd-tag--prereq">{pre}</span>)
                : <p className="acd-empty-msg">No prerequisites required</p>
              }
            </div>
          </div>

          {/* Tags */}
          <div className="acd-section">
            <div className="acd-section-title">💡 Course Tags</div>
            <div className="acd-tags">
              {tags?.length > 0
                ? tags.map((tag, i) => <span key={i} className="acd-tag acd-tag--course">{tag}</span>)
                : <p className="acd-empty-msg">No tags available</p>
              }
            </div>
          </div>

          {/* Grading Scheme */}
          <div className="acd-section">
            <div className="acd-section-title">📊 Grading Scheme</div>

            {course.labs?.length > 0 && (
              <div className="acd-grade-category">
                <button className="acd-grade-category-hdr" onClick={() => toggleCollapse('labs')} style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                  🧪 Labs <span className="acd-grade-total">Total: {roundPct(course.labs.reduce((s, l) => s + (l.gradeWeight || 0), 0))}%</span>
                  <span style={{ marginLeft: 'auto', float: 'right' }}>{collapsed['labs'] ? '▼' : '▲'}</span>
                </button>
                {!collapsed['labs'] && course.labs.map((lab, i) => (
                  <WeightRow key={i} typeKey="lab" itemId={lab.labId} idLabel={lab.labId} itemTitle={lab.title} weight={lab.gradeWeight} {...weightRowProps} />
                ))}
              </div>
            )}

            {course.quizzes?.length > 0 && (
              <div className="acd-grade-category">
                <button className="acd-grade-category-hdr" onClick={() => toggleCollapse('quizzes')} style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                  📋 Quizzes <span className="acd-grade-total">Total: {roundPct(course.quizzes.reduce((s, q) => s + (q.gradeWeight || 0), 0))}%</span>
                  <span style={{ marginLeft: 'auto', float: 'right' }}>{collapsed['quizzes'] ? '▼' : '▲'}</span>
                </button>
                {!collapsed['quizzes'] && course.quizzes.map((quiz, i) => (
                  <WeightRow key={i} typeKey="quiz" itemId={quiz.quizId} idLabel={quiz.quizId} itemTitle={quiz.title} weight={quiz.gradeWeight} {...weightRowProps} />
                ))}
              </div>
            )}

            {course.exams?.length > 0 && (
              <div className="acd-grade-category">
                <button className="acd-grade-category-hdr" onClick={() => toggleCollapse('exams')} style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                  📄 Exams <span className="acd-grade-total">Total: {roundPct(course.exams.reduce((s, e) => s + (e.gradeWeight || 0), 0))}%</span>
                  <span style={{ marginLeft: 'auto', float: 'right' }}>{collapsed['exams'] ? '▼' : '▲'}</span>
                </button>
                {!collapsed['exams'] && course.exams.map((exam, i) => (
                  <WeightRow key={i} typeKey="exam" itemId={exam.examId} idLabel={exam.examId} itemTitle={exam.title} weight={exam.gradeWeight} {...weightRowProps} />
                ))}
              </div>
            )}

            {course.capstones?.length > 0 && (
              <div className="acd-grade-category">
                <button className="acd-grade-category-hdr" onClick={() => toggleCollapse('capstones')} style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                  🎓 Capstones <span className="acd-grade-total">Total: {roundPct(course.capstones.reduce((s, c) => s + (c.gradeWeight || 0), 0))}%</span>
                  <span style={{ marginLeft: 'auto', float: 'right' }}>{collapsed['capstones'] ? '▼' : '▲'}</span>
                </button>
                {!collapsed['capstones'] && course.capstones.map((cap, i) => (
                  <WeightRow key={i} typeKey="capstone" itemId={cap.capstoneId} idLabel={cap.capstoneId} itemTitle={cap.title} weight={cap.gradeWeight} {...weightRowProps} />
                ))}
              </div>
            )}

            <div className={`acd-weight-summary ${weightIsValid ? 'acd-weight-summary--valid' : 'acd-weight-summary--invalid'}`}>
              <span className="acd-weight-summary-label">Total Course Weight:</span>
              <span className="acd-weight-summary-value">{totalWeight}% {weightIsValid ? '✅' : '⚠️'}</span>
            </div>
            {!weightIsValid && (
              <div className="acd-weight-warning">⚠️ Warning: Total weight should equal 100%</div>
            )}
          </div>

        </div>

        {/* ── Footer ── */}
        <div className="acd-footer">
          <div className="acd-footer-space" />
          <button className="acd-btn acd-btn--ghost" onClick={onClose}>Close</button>
        </div>

      </div>
    </div>
  );
};

export default AdminCourseDetailModal;
